import { useState, useEffect } from 'react';
import { toast } from '../components/ui/Toast';
import { Navigate, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { useCheckinStore } from '../stores/checkinStore';
import { NightlyWelcome } from '../components/features/checkin/NightlyWelcome';
import { BigBookQuestions } from '../components/features/checkin/BigBookQuestions';
import { MorningReflection } from '../components/features/checkin/MorningReflection';
import { AIAnalysisLoading } from '../components/features/checkin/AIAnalysisLoading';
import { InsightsCompletionScreen } from '../components/features/checkin/InsightsCompletionScreen';
import { PremiumUpsellCard } from '../components/features/checkin/PremiumUpsellCard';
import { CrisisOverlay } from '../components/features/crisis/CrisisOverlay';
import { useCrisisDetection } from '../hooks/useCrisisDetection';
import { loadTodayMorningCheckIn, saveNightlyReview } from '../services/firebase/checkin';
import { analyzeNightlyReview } from '../services/firebase/ai-analysis';
import type { AnalyzeCheckinResponse } from '../services/firebase/ai-analysis';
import type {
  NightlyReview,
  BigBookQuestionResponse,
  ApologyResponse,
  MorningIntentionReflection,
  CheckIn,
} from '../types/checkin';

type FlowStep = 'welcome' | 'bigbook' | 'reflection' | 'analyzing' | 'insights';

export function NightlyReviewPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { hasCompletedOnboarding } = useOnboardingStore();
  useCheckinStore();

  const [currentStep, setCurrentStep] = useState<FlowStep>('welcome');
  const [morningCheckIn, setMorningCheckIn] = useState<CheckIn | null>(null);
  const [analysisResponse, setAnalysisResponse] = useState<AnalyzeCheckinResponse | null>(null);

  // Store nightly review data
  const [bigBookResponses, setBigBookResponses] = useState<{
    resentful: BigBookQuestionResponse;
    selfish: BigBookQuestionResponse;
    dishonest: BigBookQuestionResponse;
    afraid: BigBookQuestionResponse;
    oweApology: ApologyResponse;
  } | null>(null);

  const [, setMorningReflections] = useState<
    Record<string, MorningIntentionReflection>
  >({});

  // Crisis detection — only active when AI analysis is available
  const { showOverlay, closeOverlay } = useCrisisDetection(
    analysisResponse?.analysis?.riskLevel || 'low'
  );

  // Route protection
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Load morning check-in on mount
  useEffect(() => {
    async function loadMorning() {
      if (!user?.uid) return;

      try {
        const morning = await loadTodayMorningCheckIn(user.uid);
        setMorningCheckIn(morning);
      } catch (error) {
        console.error('Error loading morning check-in:', error);
      }
    }

    loadMorning();
  }, [user?.uid]);

  // Handlers
  const handleWelcomeComplete = () => {
    setCurrentStep('bigbook');
  };

  const handleBigBookComplete = async (responses: {
    resentful: BigBookQuestionResponse;
    selfish: BigBookQuestionResponse;
    dishonest: BigBookQuestionResponse;
    afraid: BigBookQuestionResponse;
    oweApology: ApologyResponse;
  }) => {
    setBigBookResponses(responses);

    // Check if we have morning check-in with intentions to reflect on
    const hasMorningIntentions =
      morningCheckIn?.responses &&
      Object.keys(morningCheckIn.responses).length > 0;

    if (hasMorningIntentions) {
      setCurrentStep('reflection');
    } else {
      // Skip reflection, go straight to analysis
      await submitNightlyReview(responses, {});
    }
  };

  const handleReflectionComplete = async (
    reflections: Record<string, MorningIntentionReflection>
  ) => {
    setMorningReflections(reflections);
    if (bigBookResponses) {
      await submitNightlyReview(bigBookResponses, reflections);
    }
  };

  const handleReflectionSkip = async () => {
    if (bigBookResponses) {
      await submitNightlyReview(bigBookResponses, {});
    }
  };

  const submitNightlyReview = async (
    responses: {
      resentful: BigBookQuestionResponse;
      selfish: BigBookQuestionResponse;
      dishonest: BigBookQuestionResponse;
      afraid: BigBookQuestionResponse;
      oweApology: ApologyResponse;
    },
    reflections: Record<string, MorningIntentionReflection>
  ) => {
    if (!user?.uid) return;

    setCurrentStep('analyzing');

    try {
      // Create nightly review object
      const review: Partial<NightlyReview> = {
        date: Timestamp.now(),
        completedAt: Timestamp.now(),
        lastUpdatedAt: Timestamp.now(),
        editCount: 0,
        responses: {
          resentful: responses.resentful,
          selfish: responses.selfish,
          dishonest: responses.dishonest,
          afraid: responses.afraid,
          oweApology: responses.oweApology,
          customQuestions: {},
        },
        morningIntentionsShown: reflections,
        sharedWithSponsors: [],
      };

      // Save nightly review to Firestore first — AI analysis is secondary
      // This ensures the check-in is persisted regardless of premium status or AI errors
      await saveNightlyReview(user.uid, review as NightlyReview);

      // Call AI analysis service (returns null analysis for free/post-trial users)
      const aiAnalysisResponse = await analyzeNightlyReview(user.uid, review as NightlyReview);

      setAnalysisResponse(aiAnalysisResponse);
      setCurrentStep('insights');
    } catch (error) {
      console.error('Error submitting nightly review:', error);
      toast.error('Analysis failed', 'Please try again.');
      setCurrentStep('bigbook');
    }
  };

  const handleInsightsComplete = () => {
    navigate('/dashboard');
  };

  // Render current step
  if (currentStep === 'welcome') {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
        <div className="px-4 pt-4 pb-2">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>
        <NightlyWelcome onBegin={handleWelcomeComplete} />
      </div>
    );
  }

  if (currentStep === 'bigbook') {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
        <div className="px-4 pt-4 pb-2">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>
        <BigBookQuestions
          onComplete={handleBigBookComplete}
          onBack={() => setCurrentStep('welcome')}
        />
      </div>
    );
  }

  if (currentStep === 'reflection' && morningCheckIn) {
    // Convert morning check-in responses to format needed by MorningReflection
    // Filter out empty/default responses
    const intentions = Object.entries(morningCheckIn.responses)
      .filter(([_, response]) => {
        const answer = response.answer;
        // Filter out false booleans, empty strings, empty arrays, and zero
        if (typeof answer === 'boolean' && !answer) return false;
        if (typeof answer === 'string' && answer.trim() === '') return false;
        if (Array.isArray(answer) && answer.length === 0) return false;
        return true;
      })
      .map(([questionId, response]) => ({
        questionId,
        question: response.questionText,
        answer: response.answer,
      }));

    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
        <div className="px-4 pt-4 pb-2">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>
        <MorningReflection
          morningIntentions={intentions}
          onComplete={handleReflectionComplete}
          onBack={() => setCurrentStep('bigbook')}
          onSkip={handleReflectionSkip}
        />
      </div>
    );
  }

  if (currentStep === 'analyzing') {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
        <AIAnalysisLoading />
      </div>
    );
  }

  if (currentStep === 'insights' && analysisResponse) {
    // Premium/trial user: show full AI insights screen
    if (analysisResponse.analysis) {
      return (
        <>
          <InsightsCompletionScreen
            analysis={analysisResponse.analysis}
            onDone={handleInsightsComplete}
          />
          <CrisisOverlay
            riskLevel={analysisResponse.analysis.riskLevel}
            isOpen={showOverlay}
            onClose={closeOverlay}
          />
        </>
      );
    }

    // Free user: show upsell card in a centred layout
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-12">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Completion header */}
          <div className="text-center mb-2">
            <div className="text-5xl mb-3">✓</div>
            <h1 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
              Daily Review Complete
            </h1>
            <p className="text-surface-500 dark:text-surface-400 mt-1">
              Your reflection has been saved.
            </p>
          </div>

          {/* Upsell card */}
          <PremiumUpsellCard
            feature="AI check-in analysis"
            message="See patterns in your recovery journey"
            onDismiss={handleInsightsComplete}
          />

          {/* Done button */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleInsightsComplete}
              className="text-sm text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return <Navigate to="/dashboard" replace />;
}

export default NightlyReviewPage;
